import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar, Typography, Spin, Button, Tag, message } from 'antd';
import { UserOutlined, LockOutlined, RocketOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { PLAN_DETAILS, normalizePlan } from '../constants/plans';
import { profileKeys } from '../api/profileApi';
import { useSubscription, useCancelScheduledChange } from '../api/billingApi';

const { Title, Text } = Typography;

export const Profile = () => {
  const { currentUser, isAuthLoading, refreshMe } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = currentUser;
  const username = userData?.username || userData?.email?.split('@')[0];

  // Lemon Squeezy redirects back here after a successful checkout. The
  // webhook that actually updates the plan may lag slightly behind the
  // redirect, so refresh once immediately and again after a short delay.
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success') return;

    message.success('Payment received! Your plan will update shortly.');
    setSearchParams({}, { replace: true });

    const refresh = () => {
      refreshMe().catch(() => {});
      queryClient.invalidateQueries({ queryKey: profileKeys.details() });
    };
    refresh();
    const timeout = setTimeout(refresh, 3000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Plan comes straight off the authenticated user (/auth/me), not a
  // separate profile fetch.
  const currentPlanKey = normalizePlan(userData?.plan);
  const plan = PLAN_DETAILS[currentPlanKey];

  const { data: subscription } = useSubscription();
  const nextBillingDate = subscription?.renewsAt
    ? new Date(subscription.renewsAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const scheduledPlanName = subscription?.scheduledPlan
    ? PLAN_DETAILS[normalizePlan(subscription.scheduledPlan)]?.name
    : null;
  const scheduledDate = subscription?.scheduledChangeAt
    ? new Date(subscription.scheduledChangeAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;
  const cancelScheduledChange = useCancelScheduledChange();

  // Cancellation has a grace period — the subscription stays `cancelled`
  // (access intact) until Lemon Squeezy's `ends_at` date passes.
  const isCancelled = subscription?.status === 'cancelled';
  const endsAtDate = subscription?.endsAt
    ? new Date(subscription.endsAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const handleCancelScheduledChange = () => {
    cancelScheduledChange.mutate(undefined, {
      onSuccess: () => {
        message.success('Scheduled plan change cancelled.');
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.error || 'Unable to cancel the scheduled change.'
        );
      },
    });
  };

  const handleChangePassword = () => {
    // Mock only — route to your real change-password flow (page, modal, or email link).
    message.info('Redirecting to change password…');
  };

  const goToPlans = () => {
    navigate('/plans');
  };

  if (isAuthLoading) {
    return (
      <div className="flex w-full justify-center p-16">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full">

      <div className="profile-shell flex flex-col w-full gap-6 p-8">
        {/* Identity + account actions */}
        <div className="w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
          <div className="flex items-center gap-5">
            <span className="p-[3px] rounded-full bg-gradient-to-br from-violet-500 to-amber-400">
              <Avatar size={64} icon={<UserOutlined />} className="bg-[#1A1D29]" />
            </span>
            <div>
              <Title level={2} className="display" style={{ margin: 0, lineHeight: 1.2 }}>
                {username}
              </Title>
              <Text type="secondary">{userData?.email}</Text>
            </div>
          </div>

          <div className={`grid gap-6 mt-7 pt-6 border-t border-gray-100 ${nextBillingDate ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <div className="space-y-1">
              <Text type="secondary" className="text-sm">Current plan</Text>
              <div className="flex items-center gap-2">
                <Text strong className="capitalize text-base">{plan.name}</Text>
                <Tag color="#EDEBFC" className="!text-violet-600 !border-0">Active</Tag>
              </div>
            </div>

            <div className="space-y-1">
              <Text type="secondary" className="text-sm">Member since</Text>
              <Text strong className="text-base block">
                {userData?.createdAt
                  ? new Date(userData.createdAt).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })
                  : '-'}
              </Text>
            </div>

            {nextBillingDate && (
              <div className="space-y-1">
                <Text type="secondary" className="text-sm">Next billing date</Text>
                <Text strong className="text-base block">{nextBillingDate}</Text>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-7 pt-6 border-t border-gray-100">
            <div>
              <Text strong className="text-base block">Password</Text>
              <Text type="secondary" className="text-sm">Last changed recently.</Text>
            </div>
            <Button
              icon={<LockOutlined />}
              onClick={handleChangePassword}
              className="rounded-lg h-9"
            >
              Change password
            </Button>
          </div>
        </div>

        {/* Current plan + upgrade CTA */}
        <div className="w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-7">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center font-semibold text-xl display flex-shrink-0">
                {plan.badge}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Title level={4} className="display" style={{ margin: 0 }}>
                    {plan.name}
                  </Title>
                  <Tag color="#EDEBFC" className="!text-violet-600 !border-0">Current plan</Tag>
                </div>
                <Text type="secondary" className="text-sm">{plan.tagline}</Text>

                <ul className="mt-3 space-y-1.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <span className="w-1 h-1 rounded-full bg-violet-500" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col items-end gap-3">
              <div className="text-right">
                <span className="display text-2xl font-semibold">
                  {plan.isCustomPrice ? 'Custom' : plan.price === 0 ? '$0' : `$${plan.price}`}
                </span>
                {!plan.isCustomPrice && plan.price !== 0 && (
                  <Text type="secondary" className="ml-1.5 text-sm">/ mo</Text>
                )}
              </div>
              <Button
                type="primary"
                icon={<RocketOutlined />}
                onClick={goToPlans}
                className="bg-violet-600 hover:!bg-violet-500 border-violet-600 rounded-lg h-10 px-5"
              >
                Upgrade plan
              </Button>
            </div>
          </div>

          {scheduledPlanName && (
            <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
              <Text type="secondary" className="text-sm">
                Switching to <Text strong>{scheduledPlanName}</Text> on {scheduledDate}.
              </Text>
              <Button
                size="small"
                loading={cancelScheduledChange.isPending}
                onClick={handleCancelScheduledChange}
                className="rounded-lg"
              >
                Cancel change
              </Button>
            </div>
          )}

          {isCancelled && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <Text type="secondary" className="text-sm">
                Your subscription is cancelled. You'll keep your <Text strong>{plan.name}</Text>{' '}
                plan until <Text strong>{endsAtDate}</Text>, then move to Free.
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};